import React, { useEffect, useState } from 'react';
import { X, Shield, Swords, Trophy, Lock, ChevronRight, Activity, Crown } from 'lucide-react';
import { SupabaseService } from '../services/supabaseService';
import { Profile, War, BattleRecord, Inventory } from '../types';
import { getProfileTitle } from '../utils/gameRules';

interface ProfileDossierProps {
    profile: Profile;
    isOpen: boolean;
    onClose: () => void;
    onUpgrade?: () => void;
}

type Tab = 'overview' | 'campaigns' | 'tactics';

export const ProfileDossier: React.FC<ProfileDossierProps> = ({ profile, isOpen, onClose, onUpgrade }) => {
    const [activeTab, setActiveTab] = useState<Tab>('overview');
    const [warHistory, setWarHistory] = useState<War[]>([]);
    const [battleHistory, setBattleHistory] = useState<BattleRecord[]>([]);
    const [loading, setLoading] = useState(false);

    // Stats
    const [stats, setStats] = useState({
        wins: 0,
        losses: 0,
        draws: 0,
        streak: 0,
        bestElo: 1000,
        unitEfficiency: { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 }
    });

    useEffect(() => {
        if (isOpen && profile.id) {
            loadHistory();
        }
    }, [isOpen, profile.id]);

    const loadHistory = async () => {
        setLoading(true);
        try {
            const wars = await SupabaseService.getCampaignHistory(profile.id, 20);
            const battles = await SupabaseService.getBattleHistory(profile.id, 50);

            setWarHistory(wars);
            setBattleHistory(battles);
            calculateStats(wars, battles);
        } catch (err) {
            console.error("Failed to load dossier history", err);
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (wars: War[], battles: BattleRecord[]) => {
        let w = 0, l = 0, d = 0;
        let streak = 0;

        // War Record
        wars.forEach(war => {
            if (war.status === 'VICTORY') w++;
            else if (war.status === 'DEFEAT') l++;
            // Active wars don't count yet
        });

        // Unit Efficiency (Kills vs Deaths)
        const kills: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
        const deaths: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };

        battles.forEach(b => {
            if (b.result) {
                // Am I player or opponent?
                // BattleRecord doesn't explicitly say "I played White". 
                // It says player_id = X. 
                // If I am player_id, then casualities are MINE.
                const amHost = b.player_id === profile.id;

                const myDeaths = amHost ? b.result.casualties : b.result.enemy_casualties;
                const myKills = amHost ? b.result.enemy_casualties : b.result.casualties; // My kills are enemy deaths

                Object.keys(myDeaths).forEach(k => {
                    deaths[k as keyof Inventory] += myDeaths[k as keyof Inventory] || 0;
                });
                Object.keys(myKills).forEach(k => {
                    kills[k as keyof Inventory] += myKills[k as keyof Inventory] || 0;
                });
            }
        });

        const efficiency: any = {};
        (['p', 'n', 'b', 'r', 'q'] as const).forEach(key => {
            const k = kills[key];
            const d_count = deaths[key] || 1; // Avoid div by zero
            efficiency[key] = (k / d_count).toFixed(2);
        });

        setStats({
            wins: w,
            losses: l,
            draws: d,
            streak: 0, // Placeholder
            bestElo: profile.rating, // Placeholder
            unitEfficiency: efficiency
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-fade-in">
            <div className="w-full max-w-4xl max-h-[90vh] bg-zinc-950 border border-tudor-gold/30 rounded-lg flex flex-col md:flex-row relative shadow-2xl overflow-hidden">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-zinc-500 hover:text-white z-50 p-2"
                >
                    <X size={24} />
                </button>

                {/* Left Sidebar: ID Card */}
                <div className="w-full md:w-80 bg-zinc-900/50 border-r border-zinc-800 p-8 flex flex-col items-center text-center relative">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/black-scales.png')] opacity-20"></div>

                    <div className="relative mb-6">
                        <div className="w-24 h-24 rounded-full border-2 border-tudor-gold bg-black flex items-center justify-center shadow-[0_0_30px_rgba(180,142,61,0.2)]">
                            <span className="text-4xl">
                                {Number(stats.unitEfficiency.q) > 2 ? '♛' : '♚'}
                            </span>
                        </div>
                        {profile.is_premium && (
                            <div className="absolute -bottom-2 -right-2 bg-tudor-gold text-black rounded-full p-1.5 border border-black" title="Tribute Active">
                                <Crown size={14} fill="black" />
                            </div>
                        )}
                    </div>

                    <h2 className="text-2xl font-serif text-tudor-gold tracking-widest uppercase mb-1">{profile.username}</h2>
                    <p className="text-zinc-500 text-xs uppercase tracking-[0.2em] mb-6">{getProfileTitle(profile.rating)}</p>

                    <div className="grid grid-cols-2 gap-4 w-full mb-8">
                        <div className="bg-black/40 p-3 rounded border border-zinc-800">
                            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Rating</div>
                            <div className="text-xl font-mono text-white">{profile.rating}</div>
                        </div>
                        <div className="bg-black/40 p-3 rounded border border-zinc-800">
                            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Wins</div>
                            <div className="text-xl font-mono text-emerald-500">{stats.wins}</div>
                        </div>
                    </div>

                    <div className="mt-auto w-full">
                        <div className="flex flex-col gap-2">
                            {['Overview', 'Campaigns', 'Tactical Analysis'].map((label, idx) => {
                                const key = ['overview', 'campaigns', 'tactics'][idx] as Tab;
                                const isLocked = key === 'tactics' && !profile.is_premium;

                                return (
                                    <button
                                        key={key}
                                        onClick={() => {
                                            // Allow clicking tactics to see the lock screen
                                            setActiveTab(key);
                                        }}
                                        className={`
                                            w-full py-3 px-4 rounded text-left text-xs uppercase tracking-widest transition-all flex items-center justify-between
                                            ${activeTab === key
                                                ? 'bg-tudor-gold text-black font-bold'
                                                : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                                            }
                                        `}
                                    >
                                        <span className="flex items-center gap-2">
                                            {key === 'tactics' && isLocked && <Lock size={12} />}
                                            {label}
                                        </span>
                                        {activeTab === key && <ChevronRight size={14} />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Right Content */}
                <div className="flex-1 p-8 overflow-y-auto min-h-[500px] relative bg-zinc-950">

                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-20">
                            <div className="text-tudor-gold animate-pulse text-xs uppercase tracking-widest">Compiling Records...</div>
                        </div>
                    )}

                    {/* OVERVIEW TAB */}
                    {activeTab === 'overview' && (
                        <div className="animate-fade-in space-y-8">
                            <div>
                                <h3 className="text-lg font-serif text-zinc-200 border-b border-zinc-800 pb-2 mb-6 flex items-center gap-2">
                                    <Activity size={18} className="text-tudor-gold" />
                                    Performance Summary
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-zinc-900/40 border border-zinc-800 p-6 rounded relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-4 opacity-10">
                                            <Swords size={64} />
                                        </div>
                                        <div className="text-4xl font-mono text-white mb-2">
                                            {warHistory.length > 0
                                                ? Math.round((stats.wins / warHistory.length) * 100)
                                                : 0}
                                            <span className="text-lg text-zinc-500">%</span>
                                        </div>
                                        <div className="text-xs text-zinc-500 uppercase tracking-widest">Win Rate</div>
                                    </div>

                                    <div className="bg-zinc-900/40 border border-zinc-800 p-6 rounded">
                                        <div className="text-4xl font-mono text-white mb-2">{warHistory.length}</div>
                                        <div className="text-xs text-zinc-500 uppercase tracking-widest">Total Campaigns</div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-sm uppercase tracking-widest text-zinc-500 mb-4">Recent Activity</h3>
                                <div className="space-y-2">
                                    {warHistory.slice(0, 5).map(war => (
                                        <div key={war.id} className="flex items-center justify-between p-3 bg-zinc-900/30 border border-zinc-800/50 rounded">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-2 h-2 rounded-full ${war.status === 'VICTORY' ? 'bg-emerald-500' : war.status === 'DEFEAT' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                                                <span className="text-sm font-mono text-zinc-300">{new Date(war.created_at).toLocaleDateString()}</span>
                                            </div>
                                            <span className={`text-xs font-bold uppercase ${war.status === 'VICTORY' ? 'text-emerald-500' : war.status === 'DEFEAT' ? 'text-red-500' : 'text-yellow-500'}`}>
                                                {war.status}
                                            </span>
                                        </div>
                                    ))}
                                    {warHistory.length === 0 && (
                                        <div className="text-zinc-600 text-sm italic py-4">No recent activity recorded.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CAMPAIGNS TAB */}
                    {activeTab === 'campaigns' && (
                        <div className="animate-fade-in">
                            <h3 className="text-lg font-serif text-zinc-200 border-b border-zinc-800 pb-2 mb-6 flex items-center gap-2">
                                <Shield size={18} className="text-tudor-gold" />
                                Campaign Archives
                            </h3>

                            <div className="space-y-3">
                                {warHistory.map(war => (
                                    <div key={war.id} className="group p-4 bg-zinc-900/30 border border-zinc-800 hover:border-zinc-600 transition-colors rounded">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex flex-col">
                                                <span className="text-xs uppercase tracking-widest text-zinc-500">Operation ID</span>
                                                <span className="font-mono text-sm text-zinc-300">#{war.id.substring(0, 8)}</span>
                                            </div>
                                            <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${war.status === 'VICTORY' ? 'bg-emerald-900/20 border-emerald-500 text-emerald-400' :
                                                war.status === 'DEFEAT' ? 'bg-red-900/20 border-red-500 text-red-400' : 'bg-yellow-900/20 border-yellow-500 text-yellow-400'
                                                }`}>
                                                {war.status}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 text-xs text-zinc-400 mt-2">
                                            <span><span className="text-zinc-600 uppercase mr-1">Date:</span> {new Date(war.created_at).toLocaleDateString()}</span>
                                            <span><span className="text-zinc-600 uppercase mr-1">Battles:</span> {war.battles_fought}</span>
                                            <span><span className="text-zinc-600 uppercase mr-1">Difficulty:</span> {war.difficulty}</span>
                                        </div>
                                    </div>
                                ))}
                                {warHistory.length === 0 && (
                                    <div className="text-center py-12 text-zinc-500">
                                        No campaigns found in the archives.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TACTICAL ANALYSIS (PREMIUM) */}
                    {activeTab === 'tactics' && (
                        <div className="animate-fade-in relative h-full">
                            {!profile.is_premium && (
                                <div className="absolute inset-0 z-10 bg-zinc-950/60 backdrop-blur-sm flex flex-col items-center justify-center text-center p-8">
                                    <Lock className="w-12 h-12 text-tudor-gold mb-4" />
                                    <h3 className="text-2xl font-serif text-tudor-gold uppercase tracking-widest mb-2">Restricted Access</h3>
                                    <p className="text-zinc-400 mb-6 max-w-sm">Detailed tactical breakdowns, unit efficiency reports, and battle heatmaps are reserved for Royal Tribute contributors.</p>
                                    <button
                                        onClick={onUpgrade}
                                        className="bg-tudor-gold text-black px-8 py-3 rounded-full uppercase tracking-widest font-bold text-sm hover:scale-105 transition-transform"
                                    >
                                        Unlock Analysis
                                    </button>
                                </div>
                            )}

                            <h3 className="text-lg font-serif text-zinc-200 border-b border-zinc-800 pb-2 mb-6 flex items-center gap-2">
                                <Swords size={18} className="text-tudor-gold" />
                                Unit Efficiency Report
                            </h3>

                            <div className={`space-y-6 ${!profile.is_premium ? 'blur-sm select-none opacity-50' : ''}`}>
                                <p className="text-xs text-zinc-500 uppercase tracking-widest mb-4">Kill / Death Efficiency Ratio</p>

                                <div className="grid grid-cols-1 gap-4">
                                    {([
                                        { id: 'p', label: 'Infantry', icon: '♟' },
                                        { id: 'n', label: 'Cavalry', icon: '♞' },
                                        { id: 'b', label: 'Archers', icon: '♝' },
                                        { id: 'r', label: 'Siegeworks', icon: '♜' },
                                        { id: 'q', label: 'Command', icon: '♛' },
                                    ] as const).map(unit => {
                                        const ratio = Number(stats.unitEfficiency[unit.id] || 0);
                                        const percentage = Math.min(100, ratio * 20); // Scale 5.0 K/D to 100%

                                        return (
                                            <div key={unit.id} className="relative">
                                                <div className="flex items-center justify-between text-xs mb-1">
                                                    <span className="flex items-center gap-2 text-zinc-300">
                                                        <span className="text-lg w-4 text-center">{unit.icon}</span>
                                                        {unit.label}
                                                    </span>
                                                    <span className={`font-mono font-bold ${ratio > 1 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                        {ratio.toFixed(2)}
                                                    </span>
                                                </div>
                                                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full transition-all duration-1000 ${ratio > 1 ? 'bg-tudor-gold' : 'bg-zinc-600'}`}
                                                        style={{ width: `${percentage}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="mt-8 p-4 border border-zinc-800 bg-black/40 rounded">
                                    <h4 className="text-xs text-zinc-500 uppercase tracking-widest mb-2">Battle Heatmap (Last 30 Days)</h4>
                                    <div className="grid grid-cols-10 gap-1 opacity-60">
                                        {Array.from({ length: 50 }).map((_, i) => (
                                            <div
                                                key={i}
                                                className={`aspect-square rounded-sm ${Math.random() > 0.7 ? 'bg-tudor-gold/60' : Math.random() > 0.5 ? 'bg-tudor-gold/20' : 'bg-zinc-800'}`}
                                            ></div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
